import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BookingStatus } from 'store/bookings/types';
import { AddOnCharge } from 'store/api-client/add-on-payment-types';
import { OrderedAddOns } from '../use-booking-add-ons.hook';
import { bookingWhen } from 'utils/bookingWhen';
import { getStatusPillLabelId, getStatusPillVariant } from 'components/booking-tile/redesigned/status-pill.util';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { useLocale } from 'hooks/use-locale.hook';

import { Cta, CtaBar, Title } from '../styles';
import {
  OrderedBookingCard,
  OrderedBookingName,
  OrderedBookingWhen,
  OrderedHeader,
  OrderedIcon,
  OrderedStatusPill,
  SecondaryCta,
} from './styles';
import { OrderedAddOnsList } from './ordered-add-ons-list';
/*
 * ⭐ IMPORTED, NOT RE-DECLARED. This is the same control the booking-details card already offers for
 * the same order — see components/ordered-add-ons. Two declarations would be two answers to "what
 * does paying for an add-on look like", and the one nobody is looking at would drift.
 */
import { PayNowRow } from 'components/ordered-add-ons/styles';
import { Button } from '@hqo/ui-atoms';

/* Only the two that a zero cannot be read without. Anything else leaves `priceKnown` absent. */
const PRICE_KNOWN_BY_PRICING_TYPE: Record<string, boolean | undefined> = {
  quote_required: false,
  free: true,
};

interface AddOnsOrderedProps {
  order: OrderedAddOns;
  /*
   * RES-1336 — what was actually CHARGED, once the work order says it was. Null in every other case,
   * including a tenant who closed the sheet without paying, and the screen then reads exactly as it
   * did before this existed.
   */
  charge: AddOnCharge | null;
  /*
   * RES-1317 — work-orders is re-pricing this order, so no figure here describes it. The frozen
   * total below is the selection as it was submitted, which after an amend is the superseded one.
   * Withholding it is the honest answer until the new price lands.
   */
  isAwaitingPrice?: boolean;
  /*
   * RES-1317 — what work-orders says is owed, once it has priced the order. Preferred over the
   * frozen total because the vendor prices the job and the two can legitimately differ; null until
   * then, which is why the frozen figure is still the fallback rather than being replaced.
   */
  receiptTotal?: { labelId: string; amount: number; currency: string | null } | null;
  onViewMyBookings: VoidFunction;
  onBackToBooking: VoidFunction;
  /*
   * RES-1367 — whether this screen draws its own way out.
   *
   * ⛔ The two controls are CHROME, not part of the order, and the my-bookings dialog pins its own
   * action bar beneath a scrolling body. Drawn here as well, the tenant would get two sets of exits
   * on one screen, the lower pair scrolling away under the fixed one. The ORDER above is identical on
   * both surfaces, which is why this is a flag rather than a second confirmation screen.
   */
  withActions?: boolean;
  /*
   * VIS-2193. Draw the "✓ Add-ons ordered" heading. The my-bookings dialog turns it off: its own
   * header already says the same words, so the check moves up into that header and the dialog takes
   * over moving focus there.
   */
  withHeading?: boolean;
  /*
   * RES-1407. The confirmation's ONE way on: "View booking", to this booking in My bookings. Given,
   * it replaces "View my bookings" / "Back to booking", which under the 2-way chat redesign would be
   * the same destination twice (the old receipt is gone, so there is no "booking" to go back to).
   */
  onViewBooking?: VoidFunction;
  /*
   * RES-1367 — open billing's sheet, when there is a charge outstanding.
   *
   * ⛔ AN OFFER, NOT AN AUTOMATIC SHEET. The sheet used to open itself the moment a session existed,
   * so a tenant who had just ordered was handed a card form they never asked for, over a confirmation
   * they had not finished reading. The order stands either way and the vendor can still bill, so
   * paying is the tenant's next move to make rather than ours to make for them.
   *
   * Absent once the charge has settled, and on an order that never had anything to pay.
   */
  onPayNow?: VoidFunction;
}

/*
 * RES-1326 — "Add-ons ordered". The terminal screen of the add-on flow.
 *
 * ⛔ IT RENDERS THE FROZEN ORDER, NOT LIVE STATE, and that is not defensive tidiness. Placing the order
 * deliberately clears the selection slice and re-reads the booking, so by the time this mounts the live
 * selection is gone and the booking is no longer eligible. See `OrderedAddOns`.
 *
 * ⭐ WITH ONE EXCEPTION, AND IT IS WHY `charge` IS A SEPARATE PROP. What was actually PAID cannot be
 * frozen at submit — it does not exist yet — so it arrives live off the work order and is the only
 * thing on this screen that changes after mount. Everything else stays the estimate the picker
 * showed; the total swaps to billing's figure once there is one, because ours is a subtotal of catalog
 * prices and must never be labelled as the charge.
 *
 * ⛔ TWO THINGS THE DESIGN SHOWS THAT ARE NOT BUILDABLE, so they are absent rather than invented:
 *
 *   Tax. `submitBookingAddOns.success` is typed `void` — the epic maps the response away, so nothing
 *   the SUBMIT returns reaches this client. ⚠️ Partly outdated as of RES-1336: the work-order read
 *   behind `charge` does carry `estimatedTaxCents`, and it was null on every live order measured
 *   here (no tax configured on the test vendor). It is not rendered because a tax line that is
 *   silently absent whenever the field is null is worse than none; wire it when there is a priced
 *   example to check it against.
 *
 *   The vendor's own "In progress" pill. A Service Request's status lives in work-orders and reaches us
 *   through `resource_booking_service_request_link`; the only endpoint exposing it (RES-1056) is
 *   landlord-facing. There is no tenant read. The pill below is the BOOKING's status, which we do have.
 */
export const AddOnsOrdered = ({
  order,
  charge,
  isAwaitingPrice = false,
  receiptTotal = null,
  onViewMyBookings,
  onBackToBooking,
  withActions = true,
  withHeading = true,
  onViewBooking,
  onPayNow,
}: AddOnsOrderedProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const timeZone = useSelector(selectBuildingTimeZone);

  const { booking, partyName, partyLogoUrl, lines, total, currency, hasUnpriceable } = order;
  /*
   * Two unrelated reasons to withhold the figure, and both have to be honoured. RES-1317: the order
   * is being re-priced, so the frozen total is the superseded one. RES-1357: the basket carried a
   * line nobody had priced, so the frozen total was only ever part of the order -- and that is known
   * at submit, before any payment read lands to say so.
   *
   * ⛔ `hasUnpriceable` suppresses the FROZEN ESTIMATE ONLY, never a live receipt. It is captured at
   * submit and never changes, so an unguarded `||` keeps the screen saying "pricing is pending"
   * after the vendor has quoted and the poll has delivered a real figure. Gated on the receipt's
   * currency rather than its presence: the type admits a total with none, and ending the wait on one
   * would swap an explained absence for a bare dash.
   */
  const hasStateableReceipt = Boolean(receiptTotal?.currency);
  const awaitingPrice = isAwaitingPrice || (hasUnpriceable && !hasStateableReceipt);
  const status = booking.details?.status as BookingStatus | undefined;
  /* Resolved before it is formatted: an unmapped status yields undefined, which `formatMessage` throws on. */
  const statusLabelId = getStatusPillLabelId(status);

  /*
   * ⛔ SUBMIT UNMOUNTS THE BUTTON THAT HAD FOCUS, so without this focus falls to <body> and a screen
   * reader is told nothing — the order succeeded silently. Focusing the heading is the standard
   * route-change pattern and gets "Add-ons ordered" announced, which is the confirmation itself.
   * `tabIndex={-1}` makes the h1 programmatically focusable without adding it to the tab order.
   */
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (withHeading) {
      titleRef.current?.focus();
    }
  }, [withHeading]);

  /*
   * ⛔ `formattedFullDate` inside, rather than composing month/day/year by hand: field ORDER differs by
   * locale, and `${month} ${day}, ${year}` is US order that reads wrong everywhere else.
   */
  const when = bookingWhen({
    start: booking.details?.start_at,
    end: booking.details?.end_at,
    locale: locale as string,
    timeZone,
  });

  return (
    <>
      {withHeading && (
        <OrderedHeader>
          {/* Decorative: the heading beside it carries the meaning, and RCL ships this icon bare. */}
          <OrderedIcon aria-hidden="true" />
          <Title ref={titleRef} tabIndex={-1}>
            {intl.formatMessage({ id: 'add_on_ordered_title' })}
          </Title>
        </OrderedHeader>
      )}

      <OrderedBookingCard data-testid="booking-add-ons-ordered-booking">
        <OrderedBookingName>{booking.details?.name}</OrderedBookingName>
        {when && <OrderedBookingWhen>{when}</OrderedBookingWhen>}
        {/*
          Reuses this app's tenant-facing status vocabulary rather than printing the raw enum.
          ⚠️ It says "Reserved" for an accepted booking where the mock says "Confirmed" — deliberate:
          the shipped booking tiles already say Reserved, and agreeing with the product beats agreeing
          with one mock, at the cost of no new string.
        */}
        {!!statusLabelId && (
          <OrderedStatusPill variant={getStatusPillVariant(status)} data-testid="booking-add-ons-ordered-status">
            {intl.formatMessage({ id: statusLabelId })}
          </OrderedStatusPill>
        )}
      </OrderedBookingCard>

      {/* Our own sum of the picker's prices, under the same word the picker used: a subtotal when a
          currency can state it, the estimate hedge when none can. Billing's own figure, once there is
          one, arrives as `charge` instead. */}
      <OrderedAddOnsList
        lines={lines.map(line => ({
          ...line,
          key: line.id,
          /*
           * RES-1317. The frozen order has the catalog's own pricing type, so it says which of the
           * two zeros this is rather than inferring it: a quote-required product arrives as a zero
           * WITH a currency, exactly as a free one does. Undefined for anything else, which leaves
           * the amount to be formatted as before.
           */
          priceKnown: PRICE_KNOWN_BY_PRICING_TYPE[line.pricing_type ?? ''],
          imageUrl: line.image_url,
        }))}
        /* VIS-2193. The frozen order is built from the menu, so its lines carry the product image. */
        withProductImages
        partyName={partyName}
        /* VIS-2193. The same vendor mark the booking's own add-ons card shows; initials without one. */
        partyLogoUrl={partyLogoUrl}
        /* Withheld while work-orders re-prices, its own figure once it has one, and our arithmetic
           only until then: the frozen total is the selection as submitted, which after an amend is
           the superseded one. */
        total={
          awaitingPrice
            ? undefined
            : receiptTotal ?? { labelId: currency ? 'subtotal' : 'add_on_total_estimated', amount: total, currency }
        }
        isAwaitingPrice={awaitingPrice}
        charge={charge}
        testIdPrefix="booking-add-ons-ordered"
      />

      {onPayNow && (
        <PayNowRow>
          <Button type="button" variant="default" onClick={onPayNow} data-testid="booking-add-ons-ordered-pay-now">
            {intl.formatMessage({ id: 'add_on_pay_now' })}
          </Button>
        </PayNowRow>
      )}

      {withActions && onViewBooking && (
        <CtaBar>
          <Cta type="button" data-testid="booking-add-ons-ordered-view-booking" onClick={onViewBooking}>
            {intl.formatMessage({ id: 'add_on_view_booking' })}
          </Cta>
        </CtaBar>
      )}

      {withActions && !onViewBooking && (
        <CtaBar>
          <Cta type="button" data-testid="booking-add-ons-ordered-my-bookings" onClick={onViewMyBookings}>
            {intl.formatMessage({ id: 'add_on_view_my_bookings' })}
          </Cta>
          <SecondaryCta type="button" data-testid="booking-add-ons-ordered-back" onClick={onBackToBooking}>
            {intl.formatMessage({ id: 'add_on_back_to_booking' })}
          </SecondaryCta>
        </CtaBar>
      )}
    </>
  );
};
